import { JSX, useMemo } from 'react';
import orMiniCtaBlock from './or-mini-cta-block-a.variants';
import classNames from 'classnames';

// components
import { AtDivider } from '@/components/atoms/at-divider/at-divider';
import { AtIcon } from '@/components/atoms/at-icon/at-icon';
import { AtHeader } from '@/components/atoms/at-header/at-header';

// types
import { IOrMiniCtaBlockAProps } from './or-mini-cta-block-a.types';
import {
  EDividerColors,
  EDividerDirectionalVariants,
  EDividerVariants,
} from '@/components/atoms/at-divider/at-divider.types';

// utils + hooks
import { OrMiniCtaBlockAClient } from './or-mini-cta-block-a.client';

/**
 * `OrMiniCtaBlockA` is a compact CTA block that optionally includes an icon, a title, a short description,
 * and a CTA button or link. If a `leadGenForm` is provided, the CTA opens a modal with the form inside.
 *
 * This variant is best used when you want to optionally support lead generation without visual media.
 *
 * GTM click events are triggered on modal open, and the `interactLocation` is inferred from the CTA.
 *
 */
export const OrMiniCtaBlockA = ({
  children,
  theme,
  icon,
  content_title,
  title_element = 'h2',
  title_variant = 'h2',
  cta,
  topSpacing = 'md',
  bottomSpacing = 'md',
  topDivider = false,
  bottomDivider = false,
  leadGenForm,
}: IOrMiniCtaBlockAProps): JSX.Element => {
  const hasDescription = !!children;
  const hasTitle = !!content_title;

  const {
    base,
    contents,
    iconContainer,
    iconColor,
    title: titleClasses,
    descriptionContainer,
  } = orMiniCtaBlock({
    theme,
    hasTitle,
    hasDescription,
    topSpacing,
    bottomSpacing,
  });

  const interactionLocationNoModal = useMemo(() => {
    let bannerInteractionLocationNoModal = 'or-mini-cta-block-a';

    if (
      cta?.href?.includes('youtu.be') &&
      cta?.label?.toLowerCase().includes('tour')
    ) {
      bannerInteractionLocationNoModal = 'video-tour';
    }

    if (
      cta?.href?.includes('my.matterport') &&
      cta?.label?.toLowerCase().includes('tour')
    ) {
      bannerInteractionLocationNoModal = 'virtual-tour';
    }

    return bannerInteractionLocationNoModal;
  }, [cta?.href, cta?.label]);

  // todo: update theme to dynamic val
  return (
    <div data-theme="light">
      <aside data-testid="or-mini-cta-block-a" className="container">
        {topDivider && (
          <AtDivider
            decorative
            color={EDividerColors.GREY}
            direction={EDividerDirectionalVariants.HORIZONTAL}
            intent={EDividerVariants.THIN}
            customClasses="container"
          />
        )}
        <div className={classNames(base())}>
          <div data-theme={theme} className={contents()}>
            {icon && (
              <span className={iconContainer()}>
                <AtIcon icon={icon} color={iconColor()} />
              </span>
            )}

            <div className="flex flex-col justify-start lg:flex-row lg:items-center lg:justify-center">
              <div className="flex flex-col gap-y-6 lg:flex-row lg:items-center lg:justify-center lg:gap-x-6">
                {content_title && (
                  <AtHeader
                    variant={title_variant}
                    element={title_element}
                    className={titleClasses()}
                  >
                    {content_title}
                  </AtHeader>
                )}
                {children && (
                  <span className={descriptionContainer()}>{children}</span>
                )}
              </div>
              <OrMiniCtaBlockAClient
                leadGenForm={leadGenForm}
                interactionLocation={interactionLocationNoModal}
                cta={cta}
              />
            </div>
          </div>
        </div>
        {bottomDivider && (
          <AtDivider
            decorative
            color={EDividerColors.GREY}
            direction={EDividerDirectionalVariants.HORIZONTAL}
            intent={EDividerVariants.THIN}
            customClasses="container"
          />
        )}
      </aside>
    </div>
  );
};
